import { createSignal } from "solid-js";
import {
  type JobCategory,
  jobCategoryEnum,
} from "@/domains/job-offer/create/constants";
import type { JobSubcategoryKey } from "../types";
import {
  buildTaxonomyIndex,
  EMPTY_TAXONOMY_INDEX,
  labelOrKey,
  orderByTaxonomy,
  type TaxonomyIndex,
} from "../utils/taxonomy-index";

/**
 * Italian labels of the job categories and subcategories, from the taxonomy
 * tree the backend serves. The keys stay English and are the only value that
 * is saved, sent or compared.
 *
 * The labels are read from this signal store, never from a `useQuery` in each
 * component. In TanStack Solid Query 5.95, reading `.data` of a freshly
 * created query suspends the nearest `<Suspense>` even when the data is
 * cached, and about 21 consumers read the taxonomy, some of them mounted when
 * a dialog opens. The readers below are plain functions: they are reactive in
 * any tracked context and never suspend. Until the tree arrives, or if it
 * never does, they give the key.
 *
 * This module must not import `@/api/client` (it touches `window` and breaks
 * the node tests) nor TanStack Query: the client writes the tree here with
 * `setTaxonomyTree`, and its status with `setTaxonomyStatus`.
 *
 * See `brain/specs/freelance/taxonomy-labels/PLAN.md` (D2, G1).
 */
const [index, setIndex] = createSignal<TaxonomyIndex>(EMPTY_TAXONOMY_INDEX);

export type CategoryOption = { value: string; label: string };

/**
 * Where the tree stands: `idle` before the first load, `loading` while the
 * request is in flight, `ready` once the tree is stored, `failed` when the
 * request gave up (AC15).
 */
export type TaxonomyStatus = "idle" | "loading" | "ready" | "failed";

const [status, setStatus] = createSignal<TaxonomyStatus>("idle");

export const taxonomyStatus = (): TaxonomyStatus => status();

export const setTaxonomyStatus = (next: TaxonomyStatus): void => {
  setStatus(next);
};

/** Stores the taxonomy tree. `null` goes back to the empty index (tests). */
export function setTaxonomyTree(tree: unknown): void {
  setIndex(buildTaxonomyIndex(tree));
}

export function categoryLabel(key: string): string {
  return labelOrKey(index().categoryLabels, key);
}

export function subcategoryLabel(key: string): string {
  return labelOrKey(index().subcategoryLabels, key);
}

export function categoryOption(key: string): CategoryOption {
  return { value: key, label: categoryLabel(key) };
}

/**
 * The options of a category `Select`: always the 8 categories of
 * `jobCategoryEnum`, first those the taxonomy knows in its order, then the
 * others. Without the tree the order is the one of `jobCategoryEnum`, which is
 * the endpoint's. A tree key outside `jobCategoryEnum` never becomes an option.
 */
export function categoryOptions(): CategoryOption[] {
  return orderByTaxonomy(jobCategoryEnum, index().categoryOrder).map(
    categoryOption,
  );
}

/**
 * The subcategory keys of a category, in the order of the endpoint. A copy, so
 * the caller cannot change the store. `[]` without the tree.
 */
export function subcategoriesOf(category: string): JobSubcategoryKey[] {
  return [...(index().childrenByCategory[category] ?? [])];
}

/**
 * The category a subcategory key belongs to. `null` for a key the tree does not
 * list, and without the tree.
 */
export function parentOf(subcategory: string): JobCategory | null {
  return index().parentBySubcategory[subcategory] ?? null;
}

/** The two tree lookups, as a value a model can take in place of the store. */
export type TaxonomyLookup = {
  subcategoriesOf: typeof subcategoriesOf;
  parentOf: typeof parentOf;
};
